/**
 * Copyright (c) 2021-22 Amway
 * @author Mohit Chunawala
 */

import React from "react";
import DropdownComponent from "../../../shared-ui/dropdown";
import WhyAskingPopup from "../../../shared-ui/popup/why-asking-popup";
import WhyAskingComponent from "../../../shared-ui/why-asking";
import { getContentFromContentStack } from "../../../../core/contentStack";
import { getUpdatedOptionsList } from "../../../../utils/optionsList";
import { getWhyAskingForDropdown } from "../../../../utils/whyAskingForDropdown";

import _ from "lodash";
import { setUserResponse } from "../../../../utils/userResponse";

// Interface multiple dropdown Component Props
interface MultipleDropdownComponentProps {
  questionTitle: string;
  questionSubTitle?: string;
  inputType?: string;
  isError?: boolean;
  errorMsg: string;
  whyQuestionList: [];
  questionsList: any;
  handleChildContinue?: any;
  handleSuccessfulContinue: any;
  questionType?: string;
  recoId: any;
  contentTypeId: any;
  cmDependent: any;
}

// Interface multiple dropdown Component state
interface MultipleDropdownComponentState {
  inputValue: any;
  isEmptyValue: boolean;
  errorMsg?: string;
  isPopupOpen: boolean;
  isInputVisible: boolean;
  selectedIndex: number;
  result: any;
  dropdownContents: any;
  isEmptySelected: any;
  questionTitleState: string;
}

/**
 * Used for any answer type is in a multiple dropdown.
 *
 * @namespace MultipleDropdownComponent
 */

class MultipleDropdownComponent extends React.Component<
  MultipleDropdownComponentProps,
  MultipleDropdownComponentState
> {
  constructor(props: MultipleDropdownComponentProps) {
    super(props);
    this.state = {
      inputValue: "",
      isEmptyValue: false,
      isPopupOpen: false,
      isInputVisible: false,
      selectedIndex: 0,
      result: [],
      dropdownContents: [],
      isEmptySelected: [],
      questionTitleState: "",
    };
  }

  componentDidMount = () => {
    this.setState({
      dropdownContents: [],
      questionTitleState: this.props.questionTitle,
    });
    this.getDropdownListContents();
    this.props.handleChildContinue(this.handleError);
  };

  componentDidUpdate = () => {
    const selectedDropdown: HTMLCollectionOf<Element> =
      document.getElementsByClassName(
        "dropdown-options"
      ) as HTMLCollectionOf<Element>;
    if (selectedDropdown) {
      selectedDropdown[0]?.scrollIntoView(false);
    }
    if (this.props.questionTitle !== this.state.questionTitleState) {
      this.setState({
        questionTitleState: this.props.questionTitle,
        dropdownContents: [],
        isEmptySelected: [],
      });
      this.getDropdownListContents();
    }
  };

  /**
   * This method is used to get content for dropdown list contents
   *
   *
   */
  getDropdownListContents = async () => {
    let result: any;
    for (let index = 0; index < this.props.questionsList?.length; index++) {
      result = await this.getContentForComponent(
        this.props.questionsList[index]._content_type_uid
      );
      let Obj: any = {};
      Obj.content = result && result[0];
      Obj.isSelected = false;
      Obj.isOpen = false;

      this.setState({
        dropdownContents: [...this.state.dropdownContents, Obj],
      });
    }
  };

  /**
   * This method is used to get content from content stack
   * @param {string} contentTypeId
   *
   */
  getContentForComponent = (contentTypeId: string) => {
    return getContentFromContentStack(contentTypeId)
      .then((response) => {
        if (response && response.error_message) {
          this.setState({
            errorMsg: response,
          });
        } else {
          return response && response[0];
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * This method is used for handle input change
   * @param {Object} evt
   *
   */
  handleInputChange = (evt: any) => {
    this.setState({
      inputValue: evt.target.value,
    });
  };

  /**
   * This method is used for handle errors
   *
   *
   */
  handleError = () => {
    const tempArr: any = [];
    this.state.dropdownContents &&
      this.state.dropdownContents.map((item: any) => {
        if (item.isSelected === false) {
          tempArr.push(item.content.uid);
          this.setState({
            isEmptySelected: tempArr,
          });
        }
      });

    if (tempArr.length === 0) {
      const data: any = this.getSelectedData();
      let valuesToAdd: any = [];
      let valuesToRemove: any = [];
      for (
        let i = 0;
        i < data?.conditions?.length ||
        i < data?.dependentId?.length ||
        i < data?.recoValues?.length;
        i++
      ) {
        if (data?.conditions[i] && data?.dependentId[i]) {
          let isValueFound = _.includes(
            data?.conditions[i],
            data?.recoValues[i]
          );
          if (isValueFound) {
            valuesToRemove.push(...data?.dependentId[i]);
          } else {
            valuesToAdd.push(...data?.dependentId[i]);
          }
        }
      }

      const combinedLength = valuesToAdd?.length + valuesToRemove?.length;

      let dropDownDependency: boolean = combinedLength > 0 ? true : false;
      if (dropDownDependency && this.props.cmDependent) {
        let valuesCmAdd: Array<String> = [];
        let valuesCmRemove: Array<String> = [];
        valuesCmAdd = _.union(valuesToAdd, data.valuesToAdd);
        valuesCmRemove = _.union(valuesToRemove, data.valuesToRemove);
        this.props.handleSuccessfulContinue(
          false,
          false,
          false,
          false,
          true,
          valuesCmAdd,
          valuesCmRemove
        );
      } else if (dropDownDependency) {
        this.props.handleSuccessfulContinue(
          false,
          false,
          false,
          false,
          true,
          valuesToAdd,
          valuesToRemove
        );
      } else if (
        !_.isEmpty(data.valuesToAdd) ||
        !_.isEmpty(data.valuesToRemove)
      ) {
        this.props.handleSuccessfulContinue(
          false,
          false,
          false,
          false,
          true,
          data.valuesToAdd,
          data.valuesToRemove
        );
      } else {
        this.props.handleSuccessfulContinue();
        this.getSelectedData();
      }
    }
  };

  /**
   * @Name - getSelectedData
   * @Desc - This is used to find user response from options list
   *          and send to store.
   */
  getSelectedData = () => {
    let responseData: Object = {};
    let recoValue: Array<String> = [];
    let values: Array<String> = [];
    let recoIDs: Array<String> = [];
    // let dependentConditions: any;
    // let dependentContentTypeIds: any;
    let dependentConditions: Array<Array<String>> = [];
    let dependentContentTypeIds: Array<Array<String>> = [];
    let toRemove: Array<String> = [];
    let toAdd: Array<String> = [];
    if (!_.isEmpty(this.state.dropdownContents)) {
      _.map(this.state.dropdownContents, (data: any, index: any) => {
        if (data?.isSelected) {
          recoValue.push(data?.selectedValue?.id);
          values.push(data?.selectedValue?.value);
          recoIDs.push(data?.content?.reco_id);
          _.map(data.content?.cm_dependent, (eachData: any) => {
            if (!_.isEmpty(eachData)) {
              let isValueFound = _.includes(
                eachData?.conditions,
                data?.selectedValue?.id
              );
              if (isValueFound) {
                toRemove.push(...eachData.dependent_content_type_id);
              } else {
                toAdd.push(...eachData.dependent_content_type_id);
              }
            }
          });
          //dependentConditions.push(["0", "1"]);
          dependentConditions.push(data?.content?.dependent?.conditions);
          // dependentContentTypeIds.push(
          //   ["ms_supplements", "ms_disliked_supplements"]
          //   //data?.content?.dependent?.dependent_content_type_id
          // );
          dependentContentTypeIds.push(
            data?.content?.dependent?.dependent_content_type_id
          );
        }
      });
    }
    responseData = {
      contentTypeId: this.props.contentTypeId,
      recoId: recoIDs,
      recoValues: recoValue,
      values: values,
      conditions: dependentConditions,
      dependentId: dependentContentTypeIds,
      valuesToRemove: toRemove,
      valuesToAdd: toAdd,
    };
    setUserResponse(responseData);
    return responseData;
  };
  /**
   * This method is used for handleAsking Popup open
   *
   */
  handlePopup = () => {
    this.setState({
      isPopupOpen: true,
    });
  };

  /**
   * This method is used for handle Why Asking Popup close
   *
   */
  closePopup = () => {
    this.setState({
      isPopupOpen: false,
    });
  };

  /**
   * This method is used for handle select functionality
   * @param {any} id
   * @param {any} value
   *
   */

  handleSelect = (id: any, value: any) => {
    this.state.dropdownContents &&
      this.state.dropdownContents.map((item: any) => {
        if (item.content.uid === id) {
          item.isSelected = true;
          item.selectedValue = value;
          this.setState({
            isEmptySelected: [],
          });
        }
      });
  };
  /*This method is used to handle dropdown functionality*/
  handleDropDownClick = (value: any, isOutsideClick: boolean) => {
    const data = this.state.dropdownContents;
    data.forEach((element: any) => {
      if (isOutsideClick === true && element.content.uid === value) {
        element.isOpen = false;
      }
      if (isOutsideClick === false && element.content.uid === value) {
        element.isOpen = !element.isOpen;
      } else {
        element.isOpen = false;
      }
    });
    this.setState({ dropdownContents: data });
  };

  render() {
    const {
      questionTitle,
      questionSubTitle,
      whyQuestionList,
      questionType,
      contentTypeId,
    } = this.props;
    const { dropdownContents, isEmptySelected } = this.state;
    const updatedwhyQuestionList = getWhyAskingForDropdown(whyQuestionList);
    return (
      <div className="inner-wrapper short-answer-container">
        <WhyAskingPopup
          {...this.props}
          open={this.state.isPopupOpen}
          close={this.closePopup}
          list={updatedwhyQuestionList}
          questionType={questionType}
        />

        <div className="question-wrappper">
          <div className="question-label">{questionTitle || ""}</div>
          <div className="question-sub-label">{questionSubTitle || ""}</div>
        </div>

        {/* Multiple dropdown inner wrapper Start */}
        <div className="multiple-dropdown-wrapper">
          <div className="dropdown-wrapper">
            {dropdownContents &&
              dropdownContents.length > 0 &&
              dropdownContents.map((item: any, index: number) => (
                <DropdownComponent
                  inputType="text"
                  inputValue={(evt: any) => this.handleInputChange(evt)}
                  list={getUpdatedOptionsList(
                    item.content && item.content.options_list
                  )}
                  dropdown_title={item.content && item.content.question_label}
                  dropdown_subTitle={
                    item.content && item.content.question_subtitle
                  }
                  dropdown_placeHolder={
                    item.content && item.content.placeholder_text
                  }
                  id={item.content && item.content.uid}
                  handleDropdownButtonChange={(id: any, value: any) => {
                    this.handleSelect(id, value);
                  }}
                  errMsg={item.content && item.content.validation_errors}
                  isEmptySelected={isEmptySelected.includes(
                    item.content && item.content.uid
                  )}
                  handleDropDownClick={(value: any, isOutsideClick: boolean) =>
                    this.handleDropDownClick(value, isOutsideClick)
                  }
                  isOpen={item.isOpen}
                  contentTypeId={contentTypeId}
                  recoId={item.content && item.content.reco_id}
                />
              ))}
          </div>
        </div>
        {/* Multiple dropdown inner wrapper Ends */}
        <WhyAskingComponent {...this.props} handlePopup={this.handlePopup} />
      </div>
    );
  }
}

export default MultipleDropdownComponent;
